import { holidayCreateSchema, LANGUAGES, tenantPatchSchema } from '@beta-assur/shared';
import { useState, type FormEvent } from 'react';
import { ConfirmButton } from '../../components/confirm';
import { NoticeField } from '../../components/notice-field';
import { Field } from '../../components/page';
import { ActionError, Empty, QueryView } from '../../components/states';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Checkbox, Input, Select } from '../../components/ui/input';
import { Table, TBody, TD, TH, THead, TR } from '../../components/ui/table';
import { useApi, useAuth } from '../../lib/auth';
import { day } from '../../lib/format';
import { useI18n } from '../../lib/i18n';
import { useApiMutation, useApiQuery } from '../../lib/query';
import { useTenant } from '../../lib/referentials';
import type { HolidayList, IntegrationStatus, TenantSettings } from '../../lib/types';
import { fieldErrors } from '../portfolio-forms';

// Paramètres du cabinet (BA-026) : fenêtre d'envoi, plafond, mode d'envoi, jours fériés ;
// préavis avant échéance et fin de contrat, attestation après paiement (BA-079, BA-081).

const TIMEZONES = ['Africa/Tunis', 'Africa/Algiers', 'Africa/Casablanca', 'Europe/Paris'];

function TenantForm({ tenant }: { tenant: TenantSettings }) {
  const t = useI18n().t;
  const admin = useAuth().can('administer');
  const [form, setForm] = useState({
    name: tenant.name,
    timezone: tenant.timezone,
    localeDefault: tenant.localeDefault,
    sendWindowStart: tenant.sendWindowStart,
    sendWindowEnd: tenant.sendWindowEnd,
    sendDays: tenant.sendDays,
    dailySendCap: tenant.dailySendCap === null ? '' : String(tenant.dailySendCap),
    options: tenant.options,
    notices: tenant.notices,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);
  const save = useApiMutation<unknown>('PATCH', '/v1/tenant', {
    invalidate: ['/v1/tenant'],
    onSuccess: () => setSaved(true),
  });
  const set = (key: keyof typeof form) => (e: { target: { value: string } }) => {
    setSaved(false);
    setForm((f) => ({ ...f, [key]: e.target.value }));
  };

  function submit(event: FormEvent) {
    event.preventDefault();
    const parsed = tenantPatchSchema.safeParse({
      ...form,
      dailySendCap: form.dailySendCap.trim() === '' ? null : Number(form.dailySendCap),
    });
    if (!parsed.success) return setErrors(fieldErrors(parsed.error.issues));
    setErrors({});
    save.mutate(parsed.data);
  }

  return (
    <form
      onSubmit={submit}
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
      noValidate
    >
      <fieldset disabled={!admin} className="contents">
        <Field label={t.settings.name} error={errors.name}>
          <Input value={form.name} onChange={set('name')} />
        </Field>
        <Field label={t.settings.timezone}>
          <Select value={form.timezone} onChange={set('timezone')}>
            {[...new Set([form.timezone, ...TIMEZONES])].map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t.settings.localeDefault}>
          <Select value={form.localeDefault} onChange={set('localeDefault')}>
            {LANGUAGES.map((l) => (
              <option key={l} value={l}>
                {t.languages[l]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={`${t.settings.sendWindow} (${t.common.from})`} error={errors.sendWindowStart}>
          <Input type="time" value={form.sendWindowStart} onChange={set('sendWindowStart')} />
        </Field>
        <Field label={`${t.settings.sendWindow} (${t.common.to})`} error={errors.sendWindowEnd}>
          <Input type="time" value={form.sendWindowEnd} onChange={set('sendWindowEnd')} />
        </Field>
        <Field label={t.settings.dailyCap} hint={t.settings.noCap} error={errors.dailySendCap}>
          <Input type="number" min={1} value={form.dailySendCap} onChange={set('dailySendCap')} />
        </Field>
        <fieldset className="sm:col-span-2 lg:col-span-3">
          <legend className="mb-1 text-sm font-medium">{t.settings.sendDays}</legend>
          <div className="flex flex-wrap gap-3">
            {t.settings.days.map((label, bit) => (
              <label key={label} className="flex items-center gap-1 text-sm">
                <Checkbox
                  checked={(form.sendDays & (1 << bit)) !== 0}
                  onChange={(e) => {
                    setSaved(false);
                    setForm((f) => ({
                      ...f,
                      sendDays: e.target.checked
                        ? f.sendDays | (1 << bit)
                        : f.sendDays & ~(1 << bit),
                    }));
                  }}
                />
                {label}
              </label>
            ))}
          </div>
          {errors.sendDays ? <p className="text-xs text-destructive">{errors.sendDays}</p> : null}
        </fieldset>
        <fieldset className="grid gap-3 sm:col-span-2 sm:grid-cols-2 lg:col-span-3 lg:grid-cols-3">
          <legend className="mb-1 text-sm font-medium">{t.settings.notices}</legend>
          <NoticeField
            label={t.settings.dueNoticeDays}
            hint={t.settings.dueNoticeHint}
            value={form.notices.dueNoticeDays}
            onChange={(days) => {
              setSaved(false);
              setForm((f) => ({
                ...f,
                notices: { ...f.notices, dueNoticeDays: days ?? f.notices.dueNoticeDays },
              }));
            }}
          />
          <NoticeField
            label={t.settings.contractEndNoticeDays}
            hint={t.settings.contractEndHint}
            none={t.settings.noContractEndNotice}
            value={form.notices.contractEndNoticeDays}
            onChange={(days) => {
              setSaved(false);
              setForm((f) => ({ ...f, notices: { ...f.notices, contractEndNoticeDays: days } }));
            }}
          />
          <label className="flex items-start gap-2 self-end text-sm">
            <Checkbox
              checked={form.notices.attestationAfterPayment}
              onChange={(e) => {
                setSaved(false);
                setForm((f) => ({
                  ...f,
                  notices: { ...f.notices, attestationAfterPayment: e.target.checked },
                }));
              }}
            />
            {t.settings.attestationAfterPayment}
          </label>
          {errors.notices ? (
            <p className="text-xs text-destructive sm:col-span-2 lg:col-span-3">{errors.notices}</p>
          ) : null}
        </fieldset>
        <fieldset className="sm:col-span-2 lg:col-span-3">
          <legend className="mb-1 text-sm font-medium">{t.settings.options}</legend>
          <div className="grid gap-2">
            {(Object.keys(form.options) as (keyof TenantSettings['options'])[]).map((key) => (
              <label key={key} className="flex items-start gap-2 text-sm">
                <Checkbox
                  checked={form.options[key]}
                  onChange={(e) => {
                    setSaved(false);
                    setForm((f) => ({ ...f, options: { ...f.options, [key]: e.target.checked } }));
                  }}
                />
                {t.settings.optionLabels[key]}
              </label>
            ))}
          </div>
        </fieldset>
        {admin ? (
          <div className="flex items-center gap-3 sm:col-span-2 lg:col-span-3">
            <Button type="submit" disabled={save.isPending}>
              {t.common.save}
            </Button>
            {saved ? (
              <span role="status" className="text-sm text-success">
                {t.common.saved}
              </span>
            ) : null}
            <ActionError error={save.error} />
          </div>
        ) : null}
      </fieldset>
    </form>
  );
}

function SendingMode({ tenant }: { tenant: TenantSettings }) {
  const t = useI18n().t;
  const api = useApi();
  const admin = useAuth().can('administer');
  const query = useTenant();
  return (
    <div className="flex flex-wrap items-center gap-3">
      <Badge tone={tenant.draftMode ? 'warning' : 'success'}>
        {tenant.draftMode ? t.settings.draftOn : t.settings.draftOff}
      </Badge>
      {admin ? (
        tenant.draftMode ? (
          <ConfirmButton
            label={t.settings.activateSending}
            title={t.settings.activateSending}
            message={t.settings.activateConfirm}
            expected="ACTIVER"
            variant="default"
            onConfirm={async () => {
              await api.request('PUT', '/v1/tenant/sending', {
                body: { draftMode: false, confirm: 'ACTIVER' },
              });
              await query.refetch();
            }}
          />
        ) : (
          <ConfirmButton
            label={t.settings.backToDraft}
            title={t.settings.backToDraft}
            message={t.settings.draftOn}
            onConfirm={async () => {
              await api.request('PUT', '/v1/tenant/sending', { body: { draftMode: true } });
              await query.refetch();
            }}
          />
        )
      ) : null}
    </div>
  );
}

function IntegrationsCard() {
  const t = useI18n().t;
  const query = useApiQuery<IntegrationStatus>('/v1/tenant/integrations');
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.integrations.title}</CardTitle>
      </CardHeader>
      <CardContent>
        <QueryView query={query}>
          {(data) => (
            <div className="flex flex-col gap-3 text-sm">
              {data.sendingSuspended ? (
                <p
                  role="alert"
                  className="rounded-lg border border-destructive/30 bg-destructive-soft p-3 text-destructive"
                >
                  <strong>{t.integrations.suspended}</strong> : {data.sendingSuspended.reason}.{' '}
                  {t.integrations.suspendedHint}
                </p>
              ) : null}
              {(['connect', 'kashy'] as const).map((key) => (
                <p key={key} className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{t.integrations[key]}</span>
                  <Badge tone={data[key].configured ? 'success' : 'warning'}>
                    {data[key].configured ? t.integrations.configured : t.integrations.missing}
                  </Badge>
                  {/* Le compte de messagerie est interne à Beta Suite : seul le compte marchand est affiché. */}
                  {key === 'kashy' && data[key].accountId ? (
                    <span className="text-muted-foreground">
                      {t.integrations.account} {data[key].accountId}
                    </span>
                  ) : null}
                </p>
              ))}
            </div>
          )}
        </QueryView>
      </CardContent>
    </Card>
  );
}

function Holidays() {
  const { t, language } = useI18n();
  const api = useApi();
  const admin = useAuth().can('administer');
  const [year, setYear] = useState(new Date().getFullYear());
  const [form, setForm] = useState({ date: '', label: '' });
  const query = useApiQuery<HolidayList>('/v1/holidays', { year });
  const add = useApiMutation<unknown>('POST', '/v1/holidays', {
    invalidate: ['/v1/holidays'],
    onSuccess: () => setForm({ date: '', label: '' }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.settings.holidays}</CardTitle>
        <Input
          type="number"
          aria-label="Année"
          className="w-24"
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
        />
      </CardHeader>
      {admin ? (
        <form
          className="flex flex-wrap items-end gap-2 border-b border-border p-3"
          onSubmit={(e) => {
            e.preventDefault();
            const parsed = holidayCreateSchema.safeParse(form);
            if (parsed.success) add.mutate(parsed.data);
          }}
        >
          <Field label={t.settings.holidayDate}>
            <Input
              type="date"
              value={form.date}
              onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
            />
          </Field>
          <Field label={t.settings.holidayLabel}>
            <Input
              value={form.label}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
            />
          </Field>
          <Button type="submit" disabled={!form.date || !form.label || add.isPending}>
            {t.settings.addHoliday}
          </Button>
          <ActionError error={add.error} />
        </form>
      ) : null}
      <QueryView query={query}>
        {(data) =>
          data.items.length === 0 ? (
            <Empty />
          ) : (
            <Table>
              <THead>
                <tr>
                  <TH>{t.settings.holidayDate}</TH>
                  <TH>{t.settings.holidayLabel}</TH>
                  <TH>{t.settings.status}</TH>
                  <TH>
                    <span className="sr-only">{t.common.actions}</span>
                  </TH>
                </tr>
              </THead>
              <TBody>
                {data.items.map((h) => (
                  <TR key={h.id}>
                    <TD>{day(h.date, language)}</TD>
                    <TD>{h.label}</TD>
                    <TD className="flex gap-1">
                      <Badge>{t.settings.holidayScopes[h.scope]}</Badge>
                      <Badge tone={h.kind === 'estimated' ? 'warning' : 'neutral'}>
                        {t.settings.holidayKinds[h.kind]}
                      </Badge>
                    </TD>
                    <TD>
                      {admin && h.scope === 'tenant' ? (
                        <ConfirmButton
                          label={t.common.delete}
                          title={t.common.delete}
                          message={h.label}
                          variant="destructive"
                          onConfirm={async () => {
                            await api.request('DELETE', `/v1/holidays/${h.id}`);
                            await query.refetch();
                          }}
                        />
                      ) : null}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )
        }
      </QueryView>
    </Card>
  );
}

export function TenantSection() {
  const t = useI18n().t;
  const query = useTenant();
  return (
    <QueryView query={query}>
      {(tenant) => (
        <div className="flex flex-col gap-4">
          <Card>
            <CardHeader>
              <CardTitle>{t.settings.tenant}</CardTitle>
              <span className="text-sm text-muted-foreground">{tenant.code}</span>
            </CardHeader>
            <CardContent>
              <TenantForm key={tenant.id} tenant={tenant} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>{t.settings.sending}</CardTitle>
            </CardHeader>
            <CardContent>
              <SendingMode tenant={tenant} />
            </CardContent>
          </Card>
          <IntegrationsCard />
          <Holidays />
        </div>
      )}
    </QueryView>
  );
}
